// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { baseGridProps } from "@store/Themes/common/props/baseGrid";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";

const ChangeCustomerSelectCustomer = getWidgetComponent("ChangeCustomer/ChangeCustomerSelectCustomer");
/* Store Custom Code - End */

export interface HasErrorStatusCode {
    errorStatusCode: number | undefined;
}

export interface PageErrorWrapperProps {
    value: HasErrorStatusCode;
    badRequestMessage?: React.ReactNode;
    notFoundMessage?: React.ReactNode;
    notAuthorizedMessage?: React.ReactNode;
    customErrorMessage?: React.ReactNode;
    extendedStyles?: PageErrorWrapperStyles;
    loadFailedWrapperTestSelector?: string;
    loadFailedTextTestSelector?: string;
    // Starter Custom Code
    overridedStyles?: PageErrorWrapperStyles;
    // Store Custom Code
    isOrderApprovalDetailsPage?: boolean;
    gridItem?: InjectableCss;
}

export interface PageErrorWrapperStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    // Starter Custom Code
    loadingOverlay?: LoadingOverlayProps;
    errorInfosBox?: InfosBoxProps;
    // Store Custom Code
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
}

export const pageErrorWrapperStyles: PageErrorWrapperStyles = {
    errorInfosBox: { type: "error" },
    // Store Custom Code
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(32))}
        `,
    },
    container: baseGridProps.container,
    gridItem: {
        width: [12, 12, 12, 8, 7],
        css: css`
            flex-direction: column;
        `,
    },
};

export function PageErrorWrapper<P extends PageErrorWrapperProps>(props: React.PropsWithChildren<P>) {
    const styles = useExpandProps(pageErrorWrapperStyles, props.extendedStyles, props.overridedStyles); // Starter Custom Code
    let errorMessage;

    if (props?.value?.errorStatusCode === 400) {
        errorMessage = props?.badRequestMessage || siteMessage("Page_BadRequest");
    }

    if (props?.value?.errorStatusCode === 404) {
        errorMessage = props.notFoundMessage || siteMessage("Page_NotFound");
    }

    if (props?.value?.errorStatusCode === 403) {
        errorMessage = props.notAuthorizedMessage || siteMessage("Page_NotAuthorized");
    }

    if (props?.customErrorMessage) {
        errorMessage = props.customErrorMessage;
    }

    /* Starter Custom Code - Start */
    return (
        <DataViewLoadingOverlay
            dataView={props?.value}
            {...styles.loadingOverlay}
            data-test-selector={props.loadFailedWrapperTestSelector}
        >
            {errorMessage ? (
                <>
                    {/* Store Custom Code - Start */}
                    <InfosBox {...styles.errorInfosBox} data-test-selector={props.loadFailedTextTestSelector}>
                        {errorMessage}
                    </InfosBox>
                    {props.isOrderApprovalDetailsPage && props?.value?.errorStatusCode === 403 && (
                        <StyledWrapper {...styles.wrapper}>
                            <GridContainer {...styles.container}>
                                <GridItem {...styles.gridItem}>
                                    <ChangeCustomerSelectCustomer />
                                </GridItem>
                            </GridContainer>
                        </StyledWrapper>
                    )}
                    {/* Store Custom Code - End */}
                </>
            ) : (
                <>{props.children}</>
            )}
        </DataViewLoadingOverlay>
    );
    /* Starter Custom Code - End */
}
